import React, { useState, useEffect, useRef } from 'react';
import { X, Phone, MessageSquare, Sparkles, Flame, Send, FileText, Globe, DollarSign, Mail, ClipboardList, ChevronDown, CalendarPlus, Loader2, AlertTriangle, ChevronRight, CheckSquare, Quote, Zap, Bot, Radio, Wifi, Maximize2, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiPatch } from '../lib/api.js';
import { fmtPhone, fmtMoney, fmtFromNow, fmtDate } from '../lib/format.js';
import { openRecordingStream } from '../lib/recordings.js';
import AIIntelTab from './AIIntelTab.jsx';
import ProspectMemoryPanel from './ProspectMemoryPanel.jsx';
import ConsentBanner from './ConsentBanner.jsx';
import LeadProfileCard, { LeadProfileCardSkeleton } from './LeadProfileCard.jsx';
import ScheduleAppointmentModal from './ScheduleAppointmentModal.jsx';
import useBookEvent from '../hooks/useBookEvent.js';
import useQuickSale from './QuickSaleModal.jsx';

const STATUSES = [
  { value: 'NEW',                label: 'NEW' },
  { value: 'CONTACTED',          label: 'CONTACTED' },
  { value: 'QUALIFIED',          label: 'QUALIFIED' },
  { value: 'PRESENTATION',       label: 'PRESENTATION' },
  { value: 'FOLLOW_UP',          label: 'FOLLOW UP' },
  { value: 'CALL_BACK',          label: 'CALL BACK' },
  { value: 'BOOKED_APPOINTMENT', label: 'BOOKED APPOINTMENT', schedule: true },
  { value: 'QUOTED',             label: 'QUOTED' },
  { value: 'SOLD',               label: 'SOLD' },
  { value: 'NO_SHOW',            label: 'NO SHOW' },
  { value: 'NO_SALE',            label: 'NO SALE' },
  { value: 'NOT_INTERESTED',     label: 'NOT INTERESTED' },
  { value: 'NO_ANSWER',          label: 'NO ANSWER' },
  { value: 'HOT',                label: 'HOT' },
  { value: 'DNC',                label: 'DNC' },
];

export default function LeadDrawer({ leadId, onClose, onChange, inline = false }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('messages');
  const [smsText, setSmsText] = useState('');
  const [showReplies, setShowReplies] = useState(false);
  const repliesRef = useRef(null);

  // Preset replies for quick insert (pulled from Marketing Hub)
  const { data: repliesData } = useQuery({
    queryKey: ['preset-replies'],
    queryFn: () => apiGet('/marketing/preset-replies'),
    staleTime: 60_000,
    enabled: tab === 'messages',
  });
  const presetReplies = repliesData?.presetReplies || [];

  // Esc closes the floating drawer.
  useEffect(() => {
    if (inline) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [inline, onClose]);

  // Close replies dropdown on outside click
  useEffect(() => {
    function handleOutside(e) {
      if (repliesRef.current && !repliesRef.current.contains(e.target)) setShowReplies(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);
  const [aiBusy, setAiBusy] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [saleAmount, setSaleAmount] = useState(0);
  const { askSale, saleModal } = useQuickSale();
  const [showSchedule, setShowSchedule] = useState(false);
  const [salesForm, setSalesForm] = useState(null);
  const [salesSaving, setSalesSaving] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [smsPreview, setSmsPreview] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [scheduling, setScheduling] = useState(false);

  // AI Summary state
  const [expandedSummaries, setExpandedSummaries] = useState({});
  const [transcriptModal, setTranscriptModal] = useState(null); // summaryId to show
  const [pasteModal, setPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteUrl, setPasteUrl] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  const [summarisingCallId, setSummarisingCallId] = useState(null);

  const { data, refetch, error } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => apiGet(`/leads/${leadId}`),
    refetchInterval: 5000,
    retry: false,
  });

  const lead = data?.lead;
  const messages = data?.messages || [];
  const calls = data?.calls || [];
  const emailMessages = messages.filter((m) => m.channel === 'email');

  // Fetch AI call summaries for this lead
  const { data: summariesData, refetch: refetchSummaries } = useQuery({
    queryKey: ['call-summaries', leadId],
    queryFn: () => apiGet(`/leads/${leadId}/summaries`),
    enabled: !!leadId,
    staleTime: 10_000,
  });
  const callSummaries = summariesData?.summaries || [];

  // Fetch voice bots (owned + shared) for the bot assignment dropdowns
  const { data: voiceBotsData } = useQuery({
    queryKey: ['bots'],
    queryFn: () => apiGet('/bots'),
    staleTime: 30_000,
  });
  const voiceBots = voiceBotsData?.bots || voiceBotsData?.data || [];

  // Fetch text bots
  const { data: textBotsData } = useQuery({
    queryKey: ['text-bots'],
    queryFn: () => apiGet('/text-bots'),
    staleTime: 30_000,
  });
  const textBots = textBotsData?.bots || textBotsData?.data || [];

  // Inline save for bot assignment — fires PUT /api/leads/:id on dropdown change
  async function assignBot(field, value) {
    try {
      await apiPut(`/leads/${leadId}`, { [field]: value || null });
      refetch();
      toast.success(value ? 'Bot assigned' : 'Bot removed');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update bot assignment');
    }
  }

  // Initialize sales form when lead loads
  useEffect(() => {
    if (lead && salesForm === null) {
      setSalesForm({
        quote: lead.quote || '',
        saleAmount: lead.saleAmount || '',
        commission: lead.commission || '',
        cost: lead.cost || '',
        annualPremium: lead.annualPremium || '',
        policyId: lead.policyId || '',
        productInterest: lead.productInterest || '',
        vertical: lead.vertical || '',
      });
    }
  }, [lead, salesForm]);

  async function saveSalesInfo() {
    if (!salesForm) return;
    setSalesSaving(true);
    try {
      const payload = {};
      for (const k of Object.keys(salesForm)) {
        const v = salesForm[k];
        if (v === '' || v === null || v === undefined) continue;
        if (['quote','saleAmount','commission','cost','annualPremium'].includes(k)) {
          const n = parseFloat(v);
          if (!Number.isNaN(n)) payload[k] = n;
        } else {
          payload[k] = v;
        }
      }
      await apiPut(`/leads/${leadId}`, payload);
      toast.success('Sales info saved');
      refetch();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    setSalesSaving(false);
  }

  async function sendEmail() {
    if (!emailBody.trim()) { toast.error('Body required'); return; }
    setEmailSending(true);
    try {
      await apiPost(`/leads/${leadId}/email`, { subject: emailSubject, body: emailBody });
      setEmailSubject('');
      setEmailBody('');
      toast.success('Email queued');
      refetch();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Send failed'); }
    setEmailSending(false);
  }

  // Hooks must all run before the early returns below (rules of hooks) — this one
  // used to sit after `if (!lead)` and crashed the drawer with React #310 on load.
  // Called when the schedule modal is submitted: creates appointment then sets disposition
  const bookAppointment = useBookEvent(leadId, () => { setShowSchedule(false); refetch(); onChange?.(); });

  if (!lead) {
    return (
      <div className={inline ? 'p-5' : 'fixed inset-y-0 right-0 w-full md:w-[640px] bg-civix-panel border-l border-civix-border z-40 p-6'}>
        <div className="text-white/50 mb-2">Loading…</div>
        <LeadProfileCardSkeleton />
      </div>
    );
  }

  async function smartReply() {
    setAiBusy(true);
    try {
      const r = await apiPost('/sms/smart-reply', { leadId });
      setSmsText(r.suggestion);
    } catch { toast.error('AI unavailable'); }
    setAiBusy(false);
  }

  async function sendSms() {
    if (!smsText.trim()) return;
    try {
      const payload = { leadId, body: smsText };
      if (scheduledFor) payload.scheduledFor = new Date(scheduledFor).toISOString();
      const r = await apiPost('/sms/send', payload);
      setSmsText(''); setScheduledFor(''); setSmsPreview('');
      toast.success(r.scheduled ? 'Scheduled' : 'Sent');
      refetch();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Send failed'); }
  }

  // Spintax / token preview (Phase 2 #1).
  async function previewSms() {
    if (!smsText.trim()) { setSmsPreview(''); return; }
    try {
      const r = await apiPost('/sms/preview', { leadId, body: smsText });
      setSmsPreview(r.preview || '');
    } catch { /* ignore */ }
  }

  async function dialNow() {
    try { await apiPost('/voice/dial', { leadId }); toast.success('Call placed'); refetch(); }
    catch (e) { toast.error(e.response?.data?.error || 'Dial failed'); }
  }

  async function setStatus(status) {
    // For BOOKED_APPOINTMENT, open the scheduler modal first
    if (status === 'BOOKED_APPOINTMENT') {
      setShowSchedule(true);
      return;
    }
    try {
      const body = { leadId, status };
      if (status === 'SOLD') {
        // Lead ROI: capture annual premium so commission is credited to the vendor.
        const sale = await askSale();
        if (sale == null) return;
        body.annualPremium = sale.annualPremium;
        body.firstDraftDate = sale.firstDraftDate;
        body.saleAmount = saleAmount || 1000;
        await apiPost(`/leads/${leadId}/disposition`, body);
      } else {
        await apiPost('/dispositions', body);
      }
      toast.success(`Marked ${status}`);
      refetch();
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  }

  async function tagHot() {
    try { await apiPost(`/leads/${leadId}/hot`); toast.success('Marked HOT — rerouted'); refetch(); }
    catch { toast.error('Failed'); }
  }

  // Legacy per-drawer append helper — retained only for the AI Summary path
  // (aiSummary() below) which still writes a system-generated activity entry.
  // The user-facing composer now edits `lead.quickNote` in-place so the drawer
  // notes box and the Leads-list card stay in sync (see saveQuickNote below).
  async function addNote() {
    if (!noteText.trim()) return;
    try { await apiPost(`/leads/${leadId}/notes`, { body: noteText }); setNoteText(''); refetch(); }
    catch { toast.error('Failed'); }
  }

  // Editable single-body note — mirrors the Leads-list card RowNotes UX so an
  // edit in the drawer immediately shows up in the list and vice versa.
  async function saveQuickNote(next) {
    try {
      await (await import('../lib/api.js')).apiPut(`/leads/${leadId}`, { quickNote: next });
      refetch();
    } catch { toast.error('Failed to save note'); }
  }

  async function aiSummary() {
    setAiBusy(true);
    try {
      const r = await apiPost('/assistant/lead-summary', { leadId });
      toast.success('AI summary added');
      await apiPost(`/leads/${leadId}/notes`, { body: r.summary, type: 'ai' });
      refetch();
    } catch { toast.error('AI unavailable'); }
    setAiBusy(false);
  }

  // Phase 1: AI Call Summary helpers
  async function generateCallAiSummary(callId) {
    setSummarisingCallId(callId);
    try {
      await apiPost(`/leads/${leadId}/calls/${callId}/summarize`);
      toast.success('AI summary generated');
      refetchSummaries();
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Summary failed');
    }
    setSummarisingCallId(null);
  }

  async function submitPasteTranscript() {
    if (!pasteText.trim() && !pasteUrl.trim()) return;
    setPasteBusy(true);
    try {
      await apiPost(`/leads/${leadId}/summarize-paste`, {
        transcript:   pasteText.trim() || undefined,
        recordingUrl: pasteUrl.trim()  || undefined,
      });
      toast.success('AI summary created from transcript');
      setPasteModal(false);
      setPasteText('');
      setPasteUrl('');
      refetchSummaries();
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to generate summary');
    }
    setPasteBusy(false);
  }

  async function applyDispositionSuggestion(suggestion) {
    try {
      await apiPost(`/leads/${leadId}/disposition`, { status: suggestion });
      toast.success(`Disposition set: ${suggestion}`);
      refetch();
    } catch { toast.error('Failed to apply disposition'); }
  }

  function toggleSummaryExpand(id) {
    setExpandedSummaries((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  // 403 Forbidden: record belongs to an isolated sub-team or parallel team
  if (error?.response?.status === 403 || error?.status === 403) {
    const errorMsg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      'Access Denied: This record belongs to an isolated sub-team.';
    return (
      <div className={inline ? 'flex flex-col p-6' : 'fixed inset-y-0 right-0 w-full md:w-[640px] bg-civix-panel border-l border-civix-border z-40 flex flex-col shadow-2xl p-6 justify-center items-center'}>
        <div className="text-center space-y-4 max-w-sm">
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center text-rose-400">
            <ShieldAlert className="w-7 h-7" />
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-rose-400 font-semibold">
            403 Forbidden · Sub-Team Isolation
          </div>
          <h3 className="text-lg font-bold text-white">{errorMsg}</h3>
          <p className="text-white/60 text-xs leading-relaxed">
            This lead record is assigned to an isolated downline sub-agent. Access is restricted to direct team leads and Super Admin.
          </p>
          <div className="pt-2">
            <button onClick={onClose} className="btn-primary text-xs px-4 py-2 cursor-pointer">
              Close Drawer
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className={inline ? 'flex flex-col p-6' : 'fixed inset-y-0 right-0 w-full md:w-[640px] bg-civix-panel border-l border-civix-border z-40 flex flex-col shadow-2xl p-6 justify-center items-center'}>
        <Loader2 className="w-8 h-8 text-civix-cyan animate-spin mb-2" />
        <div className="text-xs text-white/50">Loading lead details…</div>
      </div>
    );
  }

  return (
    <div className={inline ? 'flex flex-col' : 'fixed inset-y-0 right-0 w-full md:w-[640px] bg-civix-panel border-l border-civix-border z-40 flex flex-col shadow-2xl'}>
      {/* Header */}
      <div className="p-5 border-b border-civix-border">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-display text-2xl font-bold">{lead.firstName} {lead.lastName}</div>
            <div className="text-sm text-white/50 mt-0.5">{fmtPhone(lead.phone)} · {lead.state || '—'}</div>
            <div className="flex items-center gap-2 mt-2">
              <span className={`pill ${lead.priority === 'HOT' ? 'pill-hot' : lead.priority === 'WARM' ? 'pill-warm' : 'pill-cold'}`}>
                {lead.priority === 'HOT' && <Flame className="w-3 h-3"/>}{lead.priority}
              </span>
              <span className="pill-purple">{lead.status}</span>
              {lead.optOut && <span className="pill-cold">OPT-OUT</span>}
              {lead.dnc && <span className="pill-cold bg-rose-500/15! text-rose-300!">DNC</span>}
              {lead.dncScrub?.status && lead.dncScrub.status !== 'clean' && (
                <span
                  className={`pill text-[10px] uppercase ${
                    lead.dncScrub.status === 'litigator' ? 'bg-rose-600/20! text-rose-200! border-rose-600/50!' :
                    lead.dncScrub.status === 'dnc' ? 'bg-rose-500/15! text-rose-300! border-rose-500/40!' :
                    lead.dncScrub.status === 'wireless' ? 'bg-amber-500/15! text-amber-300! border-amber-500/40!' :
                    'bg-white/5! text-white/50! border-white/10!'
                  }`}
                  title={`Last scrub: ${lead.dncScrub.checkedAt ? new Date(lead.dncScrub.checkedAt).toLocaleString() : '—'}`}
                >
                  {lead.dncScrub.status === 'litigator' ? '⚠ Litigator' : lead.dncScrub.status.toUpperCase()}
                </span>
              )}
              {Array.isArray(lead.aiTags) && lead.aiTags.map((t) => <span key={t} className="pill-purple text-[10px]">{t}</span>)}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {!inline && (
              <button
                onClick={() => navigate(`/leads/${leadId}`)}
                className="btn-ghost text-[11px] flex items-center gap-1.5"
                title="Open full lead detail"
              >
                <Maximize2 className="w-3.5 h-3.5" /> View full detail
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close lead panel"
              title="Close (Esc)"
              data-testid="lead-drawer-close"
              className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg border border-white/15 hover:bg-white/10 hover:border-white/30 cursor-pointer"
            ><X className="w-5 h-5"/></button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={dialNow} className="btn-primary text-xs flex items-center gap-1.5"><Phone className="w-3.5 h-3.5"/>Dial</button>
          <button onClick={tagHot} className="btn-ghost text-xs flex items-center gap-1.5 hover:border-red-500/60! hover:text-red-300!"><Flame className="w-3.5 h-3.5"/>Mark HOT</button>
          <button onClick={aiSummary} disabled={aiBusy} className="btn-ghost text-xs flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5"/>AI Summary</button>
        </div>

        <ConsentBanner lead={lead} onUpdated={() => onChange?.()} />
      </div>

      {/* Lead Profile — front-and-center profile data, above the tabs, so agents
          see (and can click-to-edit) every field without digging through tabs.
          Includes the Source Attribution panel at its foot. */}
      <div className="px-5">
        <LeadProfileCard lead={lead} onChange={onChange} />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-civix-border px-3">
        {['messages','email','sales','ai-intel','memory','disposition','notes','calls'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-3 text-sm capitalize border-b-2 transition ${
              tab === t ? 'border-civix-purple text-white' : 'border-transparent text-white/50 hover:text-white'
            }`}>
            {t === 'ai-intel' ? <span className="flex items-center gap-1"><Globe className="w-3 h-3"/>AI Intel</span>
              : t === 'email' ? <span className="flex items-center gap-1"><Mail className="w-3 h-3"/>Email</span>
              : t === 'sales' ? <span className="flex items-center gap-1"><DollarSign className="w-3 h-3"/>Sales/Quote</span>
              : t === 'memory' ? <span className="flex items-center gap-1"><Sparkles className="w-3 h-3"/>Memory</span>
              : t}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-5">
        {tab === 'messages' && (
          <div className="space-y-3">
            {messages.length === 0 && <div className="text-white/40 text-sm">No messages yet.</div>}
            {messages.map((m) => (
              <div key={m._id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                  m.direction === 'outbound' ? 'bg-civix-purple/30 border border-civix-purple/40' : 'bg-civix-panelLight border border-civix-border'
                }`}>
                  <div>{m.body}</div>
                  <div className="text-[10px] text-white/40 mt-1">{fmtDate(m.createdAt)} · {m.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'ai-intel' && <AIIntelTab lead={lead} />}
        {tab === 'memory' && <ProspectMemoryPanel leadId={lead?._id} />}

        {tab === 'email' && (
          <div className="space-y-3">
            <div className="label">Email Thread</div>
            <div className="space-y-2 max-h-[260px] overflow-y-auto">
              {emailMessages.length === 0 && <div className="text-white/40 text-sm">No emails yet.</div>}
              {emailMessages.map((m) => (
                <div key={m._id} className={`panel-light p-3 ${m.direction === 'outbound' ? 'border-civix-purple/40' : ''}`}>
                  <div className="flex justify-between text-[11px] text-white/40 mb-1">
                    <span className="capitalize">{m.direction} {m.status ? `· ${m.status}` : ''}</span>
                    <span>{fmtDate(m.createdAt)}</span>
                  </div>
                  {m.subject && <div className="text-sm font-semibold text-white/90 mb-1">{m.subject}</div>}
                  <div className="text-sm text-white/80 whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}
            </div>
            <div className="panel-light p-3 space-y-2">
              <div className="label">Compose</div>
              <input className="input" placeholder="Subject" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
              <textarea className="input min-h-[140px]" placeholder="Write your email…" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} />
              <div className="flex justify-between items-center">
                <div className="text-[10px] text-white/40">{lead.email || 'No email on file'}</div>
                <button onClick={sendEmail} disabled={emailSending || !lead.email} className="btn-primary text-xs flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5"/>{emailSending ? 'Sending…' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === 'sales' && salesForm && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Quote ($)</label>
                <input type="number" className="input mt-1" value={salesForm.quote} onChange={(e) => setSalesForm({...salesForm, quote: e.target.value})}/>
              </div>
              <div>
                <label className="label">Sale Amount ($)</label>
                <input type="number" className="input mt-1" value={salesForm.saleAmount} onChange={(e) => setSalesForm({...salesForm, saleAmount: e.target.value})}/>
              </div>
              <div>
                <label className="label">Commission ($)</label>
                <input type="number" className="input mt-1" value={salesForm.commission} onChange={(e) => setSalesForm({...salesForm, commission: e.target.value})}/>
              </div>
              <div>
                <label className="label">Cost ($)</label>
                <input type="number" className="input mt-1" value={salesForm.cost} onChange={(e) => setSalesForm({...salesForm, cost: e.target.value})}/>
              </div>
              <div>
                <label className="label">Annual Premium ($)</label>
                <input type="number" className="input mt-1" value={salesForm.annualPremium} onChange={(e) => setSalesForm({...salesForm, annualPremium: e.target.value})}/>
              </div>
              <div>
                <label className="label">Policy ID</label>
                <input className="input mt-1" value={salesForm.policyId} onChange={(e) => setSalesForm({...salesForm, policyId: e.target.value})}/>
              </div>
              <div>
                <label className="label">Product Interest</label>
                <input className="input mt-1" value={salesForm.productInterest} onChange={(e) => setSalesForm({...salesForm, productInterest: e.target.value})} placeholder="e.g. Term Life, IUL, Final Expense"/>
              </div>
              <div>
                <label className="label">Vertical</label>
                <select className="input mt-1" value={salesForm.vertical} onChange={(e) => setSalesForm({...salesForm, vertical: e.target.value})}>
                  <option value="">—</option>
                  <option value="life">Life</option>
                  <option value="health">Health</option>
                  <option value="medicare">Medicare</option>
                  <option value="auto">Auto</option>
                  <option value="home">Home</option>
                  <option value="final-expense">Final Expense</option>
                  <option value="annuity">Annuity</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={saveSalesInfo} disabled={salesSaving} className="btn-primary text-sm">
                {salesSaving ? 'Saving…' : 'Save Sales Info'}
              </button>
            </div>
            {(lead.saleAmount || lead.commission) ? (
              <div className="panel-light p-3 mt-2">
                <div className="label mb-1">Profit Snapshot</div>
                <div className="text-xs text-white/70 space-y-0.5">
                  <div>Sale: <span className="text-white">{fmtMoney(lead.saleAmount || 0)}</span></div>
                  <div>Commission: <span className="text-white">{fmtMoney(lead.commission || 0)}</span></div>
                  <div>Cost: <span className="text-white">{fmtMoney(lead.cost || 0)}</span></div>
                  <div className="pt-1 border-t border-civix-border">Net: <span className="text-civix-cyan">{fmtMoney((lead.commission || 0) - (lead.cost || 0))}</span></div>
                </div>
              </div>
            ) : null}

            {/* ── AI Bot Assignments ──────────────────────────────────────────── */}
            <div className="panel-light p-3 mt-2 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Bot className="w-3.5 h-3.5 text-civix-purple" />
                <span className="label">AI Bot Assignments</span>
              </div>

              {/* Voice Bot */}
              <div>
                <label className="label flex items-center gap-1.5">
                  <Radio className="w-3 h-3 text-civix-cyan" /> Voice Bot
                  <span className="text-[10px] text-white/30 font-normal ml-1">(used by AI Dialer for this lead)</span>
                </label>
                <select
                  className="input mt-1"
                  value={lead.assignedVoiceBotId || ''}
                  onChange={(e) => assignBot('assignedVoiceBotId', e.target.value)}
                >
                  <option value="">None (use campaign default)</option>
                  {voiceBots.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}{b._isOwner === false ? ' → shared' : ''}
                    </option>
                  ))}
                </select>
                {lead.assignedVoiceBotId && (
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-civix-cyan">
                    <Wifi className="w-3 h-3" />
                    Voice bot active — overrides campaign bot on next AI Dialer call
                  </div>
                )}
              </div>

              {/* Text Bot */}
              <div>
                <label className="label flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3 text-civix-purple" /> Text Bot
                  <span className="text-[10px] text-white/30 font-normal ml-1">(SMS auto-reply)</span>
                </label>
                <select
                  className="input mt-1"
                  value={lead.assignedTextBotId || ''}
                  onChange={(e) => assignBot('assignedTextBotId', e.target.value)}
                >
                  <option value="">None</option>
                  {textBots.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}{b._isOwner === false ? ' → shared' : ''}
                    </option>
                  ))}
                </select>
                {lead.assignedTextBotId && (() => {
                  const activeBot = textBots.find((b) => b._id === String(lead.assignedTextBotId));
                  return (
                    <div className={`mt-1 flex items-center gap-1 text-[10px] ${
                      activeBot?.autoReply ? 'text-emerald-400' : 'text-amber-400'
                    }`}>
                      <Wifi className="w-3 h-3" />
                      {activeBot?.autoReply
                        ? 'Auto-reply ACTIVE — bot will respond to inbound SMS'
                        : 'Bot assigned but auto-reply is OFF — enable in Text Bots settings'
                      }
                    </div>
                  );
                })()}
              </div>
            </div>
            {/* ── End AI Bot Assignments ─────────────────────────────────────── */}
          </div>
        )}

        {tab === 'disposition' && (
          <div className="space-y-4">
            {/* Marketing engine PR-A — Lead Type badge + single-value disposition picker.
                These sit ABOVE the legacy status grid so the new flow is the primary
                action while the old flow stays available during the transition. */}
            <MarketingDispositionPanel lead={lead} onChanged={() => onChange?.()} />

            <div>
              <div className="label mb-2">Set Disposition</div>
              <div className="grid grid-cols-2 gap-2">
                {STATUSES.map((s) => {
                  const isActive = lead.status === s.value;
                  const isBook = s.schedule;
                  return (
                    <button
                      key={s.value}
                      onClick={() => setStatus(s.value)}
                      className={`panel-light px-3 py-2 text-sm flex items-center justify-center gap-1.5 hover:border-civix-purple/60 hover:shadow-glow-purple transition ${
                        isActive ? 'border-civix-purple/60 shadow-glow-purple' : ''
                      } ${isBook ? 'border-civix-cyan/40! text-civix-cyan! hover:border-civix-cyan!' : ''}`}
                    >
                      {isBook && <CalendarPlus className="w-3.5 h-3.5" />}
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="panel-light p-3">
              <label className="label">Sale Amount (if SOLD)</label>
              <input type="number" className="input mt-1" value={saleAmount} onChange={(e) => setSaleAmount(parseFloat(e.target.value || 0))}/>
            </div>
            <div className="panel-light p-3">
              <div className="label mb-2">History</div>
              <div className="space-y-1.5 text-xs text-white/70 max-h-48 overflow-y-auto">
                {lead.dispositionHistory?.slice().reverse().map((h, i) => (
                  <div key={i} className="flex justify-between gap-2">
                    <span>{h.from} → <span className="text-civix-purpleSoft">{h.to}</span></span>
                    <span className="text-white/40">{fmtFromNow(h.at)}</span>
                  </div>
                ))}
                {!lead.dispositionHistory?.length && <div className="text-white/40">No history yet.</div>}
              </div>
            </div>
          </div>
        )}

        {tab === 'notes' && (
          <div className="space-y-4">
            {/* Single editable note body — same field as the Leads-list card and
                the Lead Detail Notes tab (all three bind to `lead.quickNote`).
                Debounced auto-save on blur so drawer-close doesn't lose text. */}
            <DrawerQuickNote lead={lead} onSave={saveQuickNote} />

            {/* Paste transcript button kept — that path writes to a distinct
                system entry (call summary), not a manual note. */}
            <div className="flex justify-end">
              <button onClick={() => setPasteModal(true)} className="btn-ghost text-xs flex items-center gap-1" title="Paste transcript or recording URL">
                <Zap className="w-3.5 h-3.5"/> Paste transcript
              </button>
            </div>

            {/* AI Call Summaries section */}
            {callSummaries.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-civix-purple"/>
                  <span className="text-xs font-semibold text-civix-purple uppercase tracking-wide">AI Call Summaries</span>
                  <span className="ml-auto text-xs text-white/30">{callSummaries.length}</span>
                </div>
                <div className="space-y-3">
                  {callSummaries.map((s) => {
                    const isExpanded = expandedSummaries[s._id];
                    const hasCriticalRisk = s.riskFlags?.some((r) => ['TCPA_CONCERN','COMPLIANCE_RISK','UPSET'].includes(r.type));
                    const sentimentColor = s.sentiment === 'positive' ? 'border-emerald-500/40 bg-emerald-500/5' : s.sentiment === 'negative' ? 'border-rose-500/40 bg-rose-500/5' : 'border-civix-border';
                    const sourceLabel = { ai_dialer: 'AI Dialer', civix_dialer: 'CIVIX Dialer', twilio_manual: 'Manual', manual_paste: 'Pasted' }[s.source] || s.source;
                    return (
                      <div key={s._id} className={`rounded-xl border p-3 ${sentimentColor} ${hasCriticalRisk ? 'ring-1 ring-rose-500/30' : ''}`}>
                        {/* Header row */}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className="pill-purple text-[10px]">{sourceLabel}</span>
                          <span className="text-[10px] text-white/40">{fmtFromNow(s.createdAt)}</span>
                          {s.durationSeconds > 0 && <span className="text-[10px] text-white/30">{Math.floor(s.durationSeconds/60)}m{s.durationSeconds%60}s</span>}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            s.sentiment === 'positive' ? 'bg-emerald-500/20 text-emerald-300' :
                            s.sentiment === 'negative' ? 'bg-rose-500/20 text-rose-300' :
                            'bg-white/10 text-white/50'
                          }`}>{s.sentiment}</span>
                          {/* Disposition chip */}
                          <button
                            onClick={() => applyDispositionSuggestion(s.dispositionSuggestion)}
                            className="ml-auto flex items-center gap-1 text-[10px] bg-civix-purple/20 hover:bg-civix-purple/40 text-civix-purple border border-civix-purple/30 px-2 py-0.5 rounded-full transition"
                            title={`Confidence: ${Math.round((s.dispositionConfidence||0)*100)}%`}
                          >
                            <Zap className="w-2.5 h-2.5"/>
                            Apply: {s.dispositionSuggestion}
                          </button>
                        </div>
                        {/* TL;DR */}
                        <p className="text-sm text-white/90 leading-snug mb-2">{s.tldr}</p>
                        {/* Risk flags */}
                        {s.riskFlags?.length > 0 && (
                          <div className="flex gap-1 flex-wrap mb-2">
                            {s.riskFlags.map((r, i) => (
                              <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                                ['TCPA_CONCERN','COMPLIANCE_RISK'].includes(r.type) ? 'bg-rose-500/20 text-rose-300' : 'bg-amber-500/20 text-amber-300'
                              }`}>
                                <AlertTriangle className="w-2.5 h-2.5"/>{r.type.replace(/_/g,' ')}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Key points (collapsed if > 3) */}
                        {s.keyPoints?.length > 0 && (
                          <div className="mb-2">
                            <ul className="space-y-0.5">
                              {(isExpanded ? s.keyPoints : s.keyPoints.slice(0, 3)).map((pt, i) => (
                                <li key={i} className="text-xs text-white/70 flex items-start gap-1.5"><span className="text-civix-cyan mt-0.5">•</span>{pt}</li>
                              ))}
                            </ul>
                            {s.keyPoints.length > 3 && (
                              <button onClick={() => toggleSummaryExpand(s._id)} className="text-[10px] text-civix-cyan mt-1 flex items-center gap-0.5">
                                <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}/>
                                {isExpanded ? 'Show less' : `+${s.keyPoints.length - 3} more`}
                              </button>
                            )}
                          </div>
                        )}
                        {/* Quoted highlights (expanded only) */}
                        {isExpanded && s.quotedHighlights?.length > 0 && (
                          <div className="mb-2 space-y-1">
                            {s.quotedHighlights.map((q, i) => (
                              <div key={i} className="text-xs bg-white/5 rounded-sm px-2 py-1 border-l-2 border-civix-cyan/40">
                                <span className="text-civix-cyan/70 text-[10px] uppercase">{q.speaker}: </span>
                                <span className="text-white/80 italic">"{q.quote}"</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Next steps (expanded only) */}
                        {isExpanded && s.nextSteps?.length > 0 && (
                          <div className="mb-2">
                            <div className="text-[10px] text-white/40 uppercase tracking-wide mb-1">Next Steps</div>
                            {s.nextSteps.map((ns, i) => (
                              <div key={i} className="flex items-start gap-1.5 text-xs text-white/70">
                                <CheckSquare className="w-3 h-3 text-civix-purple mt-0.5 shrink-0"/>{ns}
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Footer actions */}
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5">
                          <button onClick={() => toggleSummaryExpand(s._id)} className="text-[10px] text-white/40 hover:text-white transition">
                            {isExpanded ? 'Collapse' : 'Expand'}
                          </button>
                          {(s.transcriptText && s.transcriptText.length > 10) && (
                            <button onClick={() => setTranscriptModal(s._id)} className="text-[10px] text-civix-cyan hover:underline">
                              View Transcript
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Manual Notes section */}
            {(callSummaries.length > 0) && (
              <div className="flex items-center gap-2 my-1">
                <div className="flex-1 h-px bg-civix-border"/>
                <span className="text-[10px] text-white/30 uppercase tracking-wide">Manual Notes</span>
                <div className="flex-1 h-px bg-civix-border"/>
              </div>
            )}
            {lead.notes?.slice().reverse().map((n, i) => (
              <div key={i} className="panel-light p-3">
                <div className="flex items-center gap-2 text-xs text-white/40 mb-1">
                  <span>{n.authorName || 'System'}</span>
                  <span>·</span>
                  <span>{fmtFromNow(n.createdAt)}</span>
                  {n.type === 'ai' && <span className="pill-purple text-[10px]"><Sparkles className="w-3 h-3"/>AI</span>}
                  {n.type === 'transcription' && <span className="pill-purple text-[10px]">Call</span>}
                </div>
                <div className="text-sm text-white/90 whitespace-pre-wrap">{n.body}</div>
              </div>
            ))}
          </div>
        )}

        {tab === 'calls' && (
          <div className="space-y-3">
            {calls.length === 0 && <div className="text-white/40 text-sm">No calls yet.</div>}
            {calls.map((c) => {
              const hasSummary = callSummaries.some((s) => String(s.callId) === String(c._id));
              const isSummarising = summarisingCallId === c._id;
              const canSummarise = !hasSummary && (c.transcription || c.recordingUrl || c.transcript?.length > 0);
              return (
                <div key={c._id} className="panel-light p-3">
                  <div className="flex justify-between text-sm">
                    <span className="capitalize">{c.direction} · {c.status}</span>
                    <span className="text-white/40">{fmtFromNow(c.createdAt)}</span>
                  </div>
                  {c.duration > 0 && <div className="text-xs text-white/60">Duration: {c.duration}s</div>}
                  {c.recordingUrl && (
                    <button
                      type="button"
                      onClick={async () => {
                        const url = await openRecordingStream(c._id);
                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      className="text-xs text-civix-cyan mr-3 hover:underline"
                    >
                      Recording ↗
                    </button>
                  )}
                  {c.transcription && <div className="text-xs mt-2 text-white/70 italic">"{c.transcription.slice(0, 200)}…"</div>}
                  {c.aiTags?.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">{c.aiTags.map(t => <span key={t} className="pill-purple text-[10px]">{t}</span>)}</div>
                  )}
                  {hasSummary && (
                    <div className="mt-2 flex items-center gap-1 text-[10px] text-emerald-400">
                      <Sparkles className="w-3 h-3"/>AI Summary available — see Notes tab
                    </div>
                  )}
                  {canSummarise && !hasSummary && (
                    <button
                      onClick={() => generateCallAiSummary(c._id)}
                      disabled={isSummarising}
                      className="mt-2 btn-ghost text-xs flex items-center gap-1.5 py-1!"
                    >
                      {isSummarising
                        ? <><Loader2 className="w-3 h-3 animate-spin"/>Generating…</>
                        : <><Sparkles className="w-3 h-3"/>Generate AI Summary</>
                      }
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transcript Modal */}
      {transcriptModal && (() => {
        const s = callSummaries.find((x) => x._id === transcriptModal);
        if (!s) return null;
        return (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setTranscriptModal(null)}>
            <div className="bg-civix-panel border border-civix-border rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-civix-border">
                <span className="font-semibold text-sm">Call Transcript</span>
                <button onClick={() => setTranscriptModal(null)} className="p-1 hover:bg-civix-panelLight rounded-sm"><X className="w-4 h-4"/></button>
              </div>
              <div className="overflow-y-auto p-4">
                <pre className="text-xs text-white/80 whitespace-pre-wrap font-mono leading-relaxed">{s.transcriptText}</pre>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Paste Transcript Modal */}
      {pasteModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPasteModal(false)}>
          <div className="bg-civix-panel border border-civix-border rounded-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-civix-border">
              <span className="font-semibold text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-civix-purple"/>Paste Transcript</span>
              <button onClick={() => setPasteModal(false)} className="p-1 hover:bg-civix-panelLight rounded-sm"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-white/50 mb-1 block">Transcript text (paste raw text)</label>
                <textarea
                  className="input w-full min-h-[140px] font-mono text-xs"
                  placeholder="AGENT: Hi, is this John?&#10;LEAD: Yes, who's calling?&#10;..."
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1 block">Or recording URL (optional)</label>
                <input className="input w-full text-xs" placeholder="https://…/recording.mp3" value={pasteUrl} onChange={(e) => setPasteUrl(e.target.value)}/>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPasteModal(false)} className="btn-ghost text-sm">Cancel</button>
                <button onClick={submitPasteTranscript} disabled={pasteBusy || (!pasteText.trim() && !pasteUrl.trim())} className="btn-primary text-sm flex items-center gap-1.5">
                  {pasteBusy ? <><Loader2 className="w-4 h-4 animate-spin"/>Generating…</> : <><Sparkles className="w-4 h-4"/>Generate Summary</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer SMS bar */}
      {tab === 'messages' && (
        <div className="p-4 border-t border-civix-border space-y-2">
          <div className="flex gap-2 items-center flex-wrap">
            <button onClick={smartReply} disabled={aiBusy} className="btn-ghost text-xs flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5"/>{aiBusy ? '…' : 'Smart Reply'}
            </button>
            {/* Preset Replies insert dropdown — from /api/marketing/preset-replies */}
            <div className="relative" ref={repliesRef}>
              <button onClick={() => setShowReplies((v) => !v)} className="btn-ghost text-xs flex items-center gap-1.5" title="Insert preset reply">
                <ClipboardList className="w-3.5 h-3.5" /> Insert <ChevronDown className="w-3 h-3" />
              </button>
              {showReplies && (
                <div className="absolute bottom-full left-0 mb-1 w-72 bg-civix-panel border border-civix-border/60 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="px-3 py-2 text-[10px] text-white/40 uppercase tracking-wide border-b border-civix-border/40">Preset Replies</div>
                  {presetReplies.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-white/40">No preset replies yet — <a href="/marketing?tab=preset-replies" className="text-civix-cyan hover:underline">create some</a></div>
                  ) : (
                    <div className="max-h-56 overflow-y-auto">
                      {presetReplies.map((r) => (
                        <button key={r._id} onClick={() => { setSmsText(r.body); setShowReplies(false); }}
                          className="w-full text-left px-3 py-2.5 hover:bg-civix-panelLight transition border-b border-civix-border/30 last:border-0">
                          <div className="text-xs font-semibold text-civix-cyan">{r.name}</div>
                          <div className="text-[11px] text-white/50 mt-0.5 line-clamp-2">{r.body}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button onClick={previewSms} className="btn-ghost text-xs">Preview</button>
            <input type="datetime-local" className="input text-xs flex-1" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} title="Schedule for later (leave blank to send now)"/>
          </div>
          {smsPreview && (
            <div className="panel-light p-2 text-xs text-white/80">
              <div className="text-[10px] text-white/40 mb-0.5">Preview (spintax + tokens resolved)</div>
              {smsPreview}
            </div>
          )}
          <div className="flex gap-2">
            <input className="input flex-1" placeholder="Type a message… try {Hi|Hey} {firstName}" value={smsText} onChange={(e) => setSmsText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendSms()}/>
            <button onClick={sendSms} className="btn-primary" title={scheduledFor ? 'Schedule' : 'Send now'}><Send className="w-4 h-4"/></button>
          </div>
          <div className="text-[10px] text-white/40">Spintax: {'{Hi|Hey|Hello}'} · Tokens: {'{firstName}'}, {'{state}'}, {'{company}'} · STOP-footer auto-appended on first text + every 30 days</div>
        </div>
      )}

      {saleModal}
      {showSchedule && (
        <ScheduleAppointmentModal
          lead={lead}
          onCancel={() => setShowSchedule(false)}
          onSubmit={bookAppointment}
        />
      )}
    </div>
  );
}

// Marketing-engine PR-A — read-only Lead Type badge + a single-value
// Disposition picker that hits PATCH /api/leads/:id. PR-B will hook
// into that PATCH to fire automated actions; for now this is just a
// state edit + audit-field stamp on the server.
function MarketingDispositionPanel({ lead, onChanged }) {
  const { data: dData } = useQuery({
    queryKey: ['marketing-dispositions-picker'],
    queryFn: () => apiGet('/dispositions'),
    staleTime: 60_000,
  });
  const dispositions = dData?.dispositions || [];

  const [busy, setBusy] = useState(false);
  const currentId = lead.disposition?._id || lead.disposition || '';
  const lt = lead.leadTypeRef;
  // leadTypeRef may be a populated doc {_id, name, color} OR a bare id.
  // Render only when populated so we don't show a raw ObjectId string.
  const ltName = lt && typeof lt === 'object' ? lt.name : null;
  const ltColor = lt && typeof lt === 'object' ? lt.color : null;

  async function changeDisposition(id) {
    setBusy(true);
    try {
      await apiPatch(`/leads/${lead._id}`, { disposition: id || null });
      toast.success(id ? 'Disposition updated' : 'Disposition cleared');
      onChanged?.();
    } catch (e) {
      toast.error(e?.message || 'Failed to update disposition');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel-light p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="label">Marketing</div>
        <span className="text-[10px] text-white/40">PR-A · auto-actions in PR-B</span>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-white/40 mb-1">Lead Type</div>
        {ltName ? (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs"
            style={{ background: `${ltColor || '#7C3AED'}22`, border: `1px solid ${ltColor || '#7C3AED'}` }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: ltColor || '#7C3AED' }} />
            {ltName}
          </span>
        ) : (
          <span className="text-xs text-white/40">— (assigned by vendor on lead arrival)</span>
        )}
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wide text-white/40 mb-1 block" htmlFor="lead-disposition-picker">Disposition</label>
        <select
          id="lead-disposition-picker"
          aria-label="Disposition"
          className="input w-full text-sm"
          disabled={busy}
          value={currentId}
          onChange={(e) => changeDisposition(e.target.value)}
        >
          <option value="">— None —</option>
          {dispositions.map((d) => (
            <option key={d._id} value={d._id}>{d.label || d.name}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// Editable single-body note used inside the drawer's Notes tab. Same UX as
// the Leads-list card's RowNotes: debounced auto-save, save-on-blur, small
// status pill. All three surfaces (Leads-list card, drawer, Lead Detail Notes
// tab) bind to `lead.quickNote` so edits flow between them.
function DrawerQuickNote({ lead, onSave }) {
  const [val, setVal] = useState(lead?.quickNote || '');
  const [state, setState] = useState('idle');
  const initialRef = useRef(lead?.quickNote || '');
  const timerRef = useRef(null);

  useEffect(() => {
    if (state === 'idle' && (lead?.quickNote || '') !== initialRef.current) {
      setVal(lead?.quickNote || '');
      initialRef.current = lead?.quickNote || '';
    }
  }, [lead?.quickNote]); // eslint-disable-line react-hooks/exhaustive-deps

  async function flush(next) {
    setState('saving');
    try {
      await onSave(next);
      initialRef.current = next;
      setState('saved');
      setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch {
      setState('error');
    }
  }

  function onChangeText(e) {
    const next = e.target.value;
    setVal(next);
    setState('typing');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(next), 1200);
  }

  function onBlur() {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val !== initialRef.current) flush(val);
  }

  return (
    <div className="rounded-lg bg-civix-panel border border-civix-border p-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-white/50">Notes</span>
        <span className="text-[10px] text-white/40">
          {state === 'typing' && 'Editing…'}
          {state === 'saving' && 'Saving…'}
          {state === 'saved' && 'Saved'}
          {state === 'error' && <span className="text-red-300/80">Save failed</span>}
        </span>
      </div>
      <textarea
        value={val}
        onChange={onChangeText}
        onBlur={onBlur}
        placeholder="What happened on this call/text? Callback times, objections, family details…"
        rows={6}
        className="w-full resize-y min-h-[120px] rounded-md bg-civix-bg border border-civix-border
                   px-3 py-2 text-sm text-white/90 focus:border-civix-purple/60 outline-hidden
                   placeholder:text-white/30"
      />
      <div className="mt-1 text-[10px] text-white/35">
        Auto-saves as you type · Same box as the Leads list card
      </div>
    </div>
  );
}
